/**
 * A text box that also offers a list (`ui/suggestions.tsx`): type anything, or
 * pick one. Typing filters the list; the ▾ button or ↓ shows all of it.
 */

import { useRef, useState, type FocusEventHandler } from "react";
import { ChevronDown } from "lucide-react";
import { SuggestionList, useSuggestions, type Suggestion } from "./suggestions";

export type ComboOption = Suggestion;

export function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  invalid,
  onFocus,
  mono = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboOption[];
  placeholder?: string;
  invalid?: boolean | undefined;
  onFocus?: FocusEventHandler<HTMLInputElement> | undefined;
  mono?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState(false);
  const q = value.trim().toLowerCase();
  const shown = typed && q ? options.filter((o) => o.value.toLowerCase().includes(q)) : options;
  const pick = (o: Suggestion) => (onChange(o.value), setTyped(false), list.close());
  const list = useSuggestions(shown, pick);
  const show = () => (setTyped(false), list.show(options.findIndex((o) => o.value === value)));

  return (
    <div ref={box} className="relative min-w-0">
      <input
        {...list.inputProps}
        aria-label={label}
        aria-invalid={invalid || undefined}
        className={`inp w-full pr-7 text-xs ${mono ? "font-mono" : ""}`}
        placeholder={placeholder}
        value={value}
        onFocus={(e) => (show(), onFocus?.(e))}
        onClick={() => !list.open && show()}
        onBlur={list.close}
        onKeyDown={list.onKeyDown}
        onChange={(e) => (onChange(e.target.value), setTyped(true), list.show(0))}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        className="absolute top-1/2 right-1.5 -translate-y-1/2 text-faint hover:text-ink"
        onMouseDown={(e) => (e.preventDefault(), list.open ? list.close() : show())}
      >
        <ChevronDown size={13} />
      </button>
      <SuggestionList anchor={box} state={list} options={shown} label={label} onPick={pick} mono={mono} />
    </div>
  );
}
